import React from 'react';
import { Card, Icon } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';

interface MetricsCardProps {
  title: string;
  value: string | number;
  icon: string;
  iconColor?:
    | 'primary'
    | 'secondary'
    | 'info'
    | 'success'
    | 'warning'
    | 'error'
    | 'dark';
  percentage?: {
    value: number;
    period: string;
  };
  loading?: boolean;
}

const MetricsCard: React.FC<MetricsCardProps> = ({
  title,
  value,
  icon,
  iconColor = 'info',
  percentage,
  loading = false,
}) => {
  const formatValue = (val: string | number) => {
    if (typeof val === 'number') {
      if (val >= 1000000) {
        return `${(val / 1000000).toFixed(1)}M`;
      } else if (val >= 1000) {
        return `${(val / 1000).toFixed(1)}K`;
      }
      return val.toLocaleString();
    }
    return val;
  };

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(val);
  };

  const displayValue =
    title.toLowerCase().includes('value') || title.toLowerCase().includes('$')
      ? formatCurrency(Number(value))
      : formatValue(value);

  return (
    <Card sx={{ height: '100%' }}>
      <MDBox p={3}>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <MDBox>
            <MDTypography
              variant="button"
              fontWeight="medium"
              color="text"
              textTransform="capitalize"
            >
              {title}
            </MDTypography>
            <MDBox mt={1}>
              {loading ? (
                <MDTypography variant="h4" color="text">
                  --
                </MDTypography>
              ) : (
                <MDTypography variant="h4" fontWeight="bold" color="dark">
                  {displayValue}
                </MDTypography>
              )}
            </MDBox>
            {percentage && !loading && (
              <MDBox mt={1} display="flex" alignItems="center">
                <MDTypography
                  variant="button"
                  fontWeight="bold"
                  color={percentage.value >= 0 ? 'success' : 'error'}
                >
                  {percentage.value >= 0 ? '+' : ''}
                  {percentage.value.toFixed(1)}%
                </MDTypography>
                <MDTypography variant="button" color="text" ml={0.5}>
                  vs {percentage.period}
                </MDTypography>
              </MDBox>
            )}
          </MDBox>
          <MDBox
            width="3rem"
            height="3rem"
            borderRadius="50%"
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{
              backgroundColor: ({ palette }: Theme) =>
                palette[iconColor as keyof Theme['palette']].main,
              color: 'white',
            }}
          >
            <Icon fontSize="medium">{icon}</Icon>
          </MDBox>
        </MDBox>
      </MDBox>
    </Card>
  );
};

export default MetricsCard;
